import { useEffect, useRef, type ReactNode } from "react";

import { cn } from "@/utils/utils";

/**
 * A popover menu built on `<details>`.
 *
 * No state, no portal, and Escape and click-away handled once here rather than
 * three times across the app. `<details>` alone stays open on an outside click,
 * which is the one thing a menu must not do.
 */
export function Menu({
  trigger,
  align = "end",
  side = "bottom",
  width = "w-56",
  className,
  children,
}: {
  /** Rendered inside the `<summary>`, so it must not itself be a button. */
  trigger: ReactNode;
  align?: "start" | "end";
  /** `top` for a trigger that sits at the foot of its column and has no room below. */
  side?: "top" | "bottom";
  width?: string;
  className?: string;
  children: ReactNode;
}) {
  const root = useRef<HTMLDetailsElement>(null);
  const close = () => {
    if (root.current) root.current.open = false;
  };

  useEffect(() => {
    const handle = (event: Event) => {
      const element = root.current;
      if (!element?.open) return;
      if (
        event instanceof KeyboardEvent
          ? event.key === "Escape"
          : !element.contains(event.target as Node)
      ) {
        element.open = false;
      }
    };
    document.addEventListener("pointerdown", handle);
    document.addEventListener("keydown", handle);
    return () => {
      document.removeEventListener("pointerdown", handle);
      document.removeEventListener("keydown", handle);
    };
  }, []);

  return (
    <details ref={root} className={cn("group relative", className)}>
      <summary className="cursor-pointer list-none [&::-webkit-details-marker]:hidden">
        {trigger}
      </summary>
      <div
        className={cn(
          "border-line bg-surface-raised absolute z-40 rounded-xl border p-1",
          "animate-sheet shadow-[var(--shadow-float)]",
          // The panel grows away from the trigger, so it opens into the space
          // there actually is rather than off the end of the column.
          side === "top" ? "bottom-full mb-1.5 origin-bottom" : "top-full mt-1.5 origin-top",
          align === "end" ? "right-0" : "left-0",
          width,
        )}
        onClick={(event) => {
          // Any activation inside closes the menu, unless it opts out.
          if (!(event.target as HTMLElement).closest("[data-keep-open]")) close();
        }}
      >
        {children}
      </div>
    </details>
  );
}

/**
 * The shape of a menu row, exported on its own so a `<Link>` can wear it — a
 * row that navigates must be an anchor, not a button with an onClick.
 */
export const menuItemClass = (danger = false) =>
  cn(
    "text-callout flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left",
    "transition-colors duration-[var(--duration-fast)]",
    "disabled:pointer-events-none disabled:opacity-40",
    danger ? "text-danger hover:bg-danger-soft" : "text-fg hover:bg-surface-inset",
    "[&_svg]:size-4 [&_svg]:shrink-0",
  );

/** One row in a menu. `danger` is the only tone; everything else is plain. */
export function MenuItem({
  danger = false,
  icon,
  trailing,
  className,
  children,
  ...props
}: React.ComponentProps<"button"> & {
  danger?: boolean;
  icon?: ReactNode;
  trailing?: ReactNode;
}) {
  return (
    <button
      type="button"
      role="menuitem"
      className={cn(menuItemClass(danger), className)}
      {...props}
    >
      {icon}
      <span className="min-w-0 flex-1 truncate">{children}</span>
      {trailing ? <span className="text-fg-subtle text-subhead">{trailing}</span> : null}
    </button>
  );
}

export function MenuSeparator() {
  return <hr className="border-line my-1 border-0 border-t" />;
}

export function MenuLabel({ children }: { children: ReactNode }) {
  return (
    <p className="text-fg-subtle text-footnote px-2.5 pt-2 pb-1 font-medium">{children}</p>
  );
}
